/* eslint-disable camelcase */
import {
  ICmnInspectionProps,
  IInspectionFirstClassificationProps,
  IInspectionSecondClassificationProps,
  IInspectionFinalStandardProps,
  IInspectionHook,
} from 'interface/inspection/InspectionInfo';
import { IInspectionDetailProps, Question } from 'interface/project/projectInfo';
import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  changeClassification,
  changeDetailProgressInfo,
  changeInspectionDetail,
  changeInspectionFirstClassification,
  updateInspectionFirstClassificationItem,
  updateInspectionSecondClassificationItem,
  initInspectionThirdClassification,
  updateInspectionThirdClassificationItem,
  initInspectionEndClassification,
  initInspectionFinalBase,
  autoFillInspectionFinalBase,
  removeItemsFromInspectionEndClassification,
  changeInspectionFinalBase,
  changeSurveyAdd,
  changeIsClassificationGuideStart1,
  changeIsClassificationGuideStart2_1,
  changeIsClassificationGuideStart2_2,
  changeIsClassificationGuideStart2_3,
  changeIsClassificationGuideStart2_4,
  changeIsClassificationGuideStart3,
  changeIsClassificationGuideStartAdditional,
  changeIsClassificationGuideStartComplete,
  changeIsClassificationGuideStartFinal,
  changeObjectKeyIndex,
  changeProgressInfo,
  changeResultInspection,
  inspectionSelector,
} from 'store/inspection/inspectionSlice';

export const useInspection = (): IInspectionHook => {
  const dispatch = useDispatch();
  const inspectionState = useSelector(inspectionSelector);
  return {
    ...inspectionState,
    changeProgressInfo: useCallback(
      (_progressInfo: number) => {
        dispatch(changeProgressInfo({ progressInfo: _progressInfo }));
      },
      [dispatch],
    ),
    changeDetailProgressInfo: useCallback(
      (_detailProgressInfo: number) => {
        dispatch(changeDetailProgressInfo({ detailProgressInfo: _detailProgressInfo }));
      },
      [dispatch],
    ),
    changeObjectKeyIndex: useCallback(
      (_objectKeyIndex: number) => {
        dispatch(changeObjectKeyIndex({ objectKeyIndex: _objectKeyIndex }));
      },
      [dispatch],
    ),
    changeInspectionDetail: useCallback(
      (_inspectionDetail: IInspectionDetailProps) => {
        dispatch(changeInspectionDetail({ inspectionDetail: _inspectionDetail }));
      },
      [dispatch],
    ),
    changeClassification: useCallback(
      (_classification: string) => {
        dispatch(changeClassification({ classification: _classification }));
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart1: useCallback(
      (_isClassificationGuideStart1: boolean) => {
        dispatch(changeIsClassificationGuideStart1({ isClassificationGuideStart1: _isClassificationGuideStart1 }));
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart2_1: useCallback(
      (_isClassificationGuideStart2: boolean) => {
        dispatch(changeIsClassificationGuideStart2_1({ isClassificationGuideStart2_1: _isClassificationGuideStart2 }));
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart2_2: useCallback(
      (_isClassificationGuideStart2_2: boolean) => {
        dispatch(
          changeIsClassificationGuideStart2_2({ isClassificationGuideStart2_2: _isClassificationGuideStart2_2 }),
        );
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart2_3: useCallback(
      (_isClassificationGuideStart2_3: boolean) => {
        dispatch(
          changeIsClassificationGuideStart2_3({ isClassificationGuideStart2_3: _isClassificationGuideStart2_3 }),
        );
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart2_4: useCallback(
      (_isClassificationGuideStart2_4: boolean) => {
        dispatch(
          changeIsClassificationGuideStart2_4({ isClassificationGuideStart2_4: _isClassificationGuideStart2_4 }),
        );
      },
      [dispatch],
    ),
    changeIsClassificationGuideStart3: useCallback(
      (_isClassificationGuideStart3: boolean) => {
        dispatch(changeIsClassificationGuideStart3({ isClassificationGuideStart3: _isClassificationGuideStart3 }));
      },
      [dispatch],
    ),
    changeIsClassificationGuideStartFinal: useCallback(
      (_isClassificationGuideStartFinal: boolean) => {
        dispatch(
          changeIsClassificationGuideStartFinal({ isClassificationGuideStartFinal: _isClassificationGuideStartFinal }),
        );
      },
      [dispatch],
    ),
    changeIsClassificationGuideStartAdditional: useCallback(
      (_isClassificationGuideStartAdditional: boolean) => {
        dispatch(
          changeIsClassificationGuideStartAdditional({
            isClassificationGuideStartAdditional: _isClassificationGuideStartAdditional,
          }),
        );
      },
      [dispatch],
    ),
    changeIsClassificationGuideStartComplete: useCallback(
      (_isClassificationGuideStartComplete: boolean) => {
        dispatch(
          changeIsClassificationGuideStartComplete({
            isClassificationGuideStartComplete: _isClassificationGuideStartComplete,
          }),
        );
      },
      [dispatch],
    ),
    changeResultInspection: useCallback(
      (_obj: ICmnInspectionProps) => {
        dispatch(changeResultInspection({ ..._obj }));
      },
      [dispatch],
    ),
    changeInspectionFirstClassification: useCallback(
      (_inspectionFirstClassification: IInspectionFirstClassificationProps) => {
        dispatch(
          changeInspectionFirstClassification({ inspectionFirstClassification: _inspectionFirstClassification }),
        );
      },
      [dispatch],
    ),
    updateInspectionFirstClassificationItem: useCallback(
      (_parent: 'NN' | 'N' | 'A' | 'AA', _obj: ICmnInspectionProps, _oldParent?: 'NN' | 'N' | 'A' | 'AA') => {
        dispatch(updateInspectionFirstClassificationItem({ parent: _parent, item: _obj, oldParent: _oldParent }));
      },
      [dispatch],
    ),
    updateInspectionSecondClassificationItem: useCallback(
      (
        _type: keyof IInspectionSecondClassificationProps,
        _obj: ICmnInspectionProps,
        _oldType?: keyof IInspectionSecondClassificationProps,
      ) => {
        dispatch(updateInspectionSecondClassificationItem({ type: _type, item: _obj, oldType: _oldType }));
      },
      [dispatch],
    ),
    initInspectionThirdClassification: useCallback(
      (_groups: string[]) => {
        dispatch(initInspectionThirdClassification({ groups: _groups }));
      },
      [dispatch],
    ),
    updateInspectionThirdClassificationItem: useCallback(
      (_type: string, _obj: ICmnInspectionProps, _oldType?: string) => {
        dispatch(updateInspectionThirdClassificationItem({ type: _type, item: _obj, oldType: _oldType }));
      },
      [dispatch],
    ),
    initInspectionEndClassification: useCallback(() => {
      dispatch(initInspectionEndClassification({}));
    }, [dispatch]),
    initInspectionFinalBase: useCallback(
      (_grid: string) => {
        dispatch(initInspectionFinalBase({ grid: _grid }));
      },
      [dispatch],
    ),
    autoFillInspectionFinalBase: useCallback(() => {
      dispatch(autoFillInspectionFinalBase());
    }, [dispatch]),
    removeItemsFromInspectionEndClassification: useCallback(
      (_key: string, _items: ICmnInspectionProps[]) => {
        dispatch(removeItemsFromInspectionEndClassification({ key: _key, items: _items }));
      },
      [dispatch],
    ),
    changeInspectionFinalBase: useCallback(
      (_obj: IInspectionFinalStandardProps) => {
        dispatch(changeInspectionFinalBase(_obj));
      },
      [dispatch],
    ),
    changeSurveyAdd: useCallback(
      (_surveyAdd: Question[]) => {
        dispatch(changeSurveyAdd({ surveyAdd: _surveyAdd }));
      },
      [dispatch],
    ),
  };
};
export default useInspection;
